import { User, Mail, Shield, Wrench } from 'lucide-react';
import type { UserRole } from 'scheduler-types';
import type { PersonListItem, PersonSkill } from '../hooks/usePeople';

const roleColors: Record<UserRole, string> = {
  planner: 'bg-purple-100 text-purple-700',
  worker: 'bg-blue-100 text-blue-700',
  contractor: 'bg-orange-100 text-orange-700',
  account_manager: 'bg-green-100 text-green-700',
  leadership: 'bg-indigo-100 text-indigo-700',
  procurement: 'bg-teal-100 text-teal-700',
  viewer: 'bg-gray-100 text-gray-700',
};

const roleLabels: Record<UserRole, string> = {
  planner: 'Planner',
  worker: 'Worker',
  contractor: 'Contractor',
  account_manager: 'Account Manager',
  leadership: 'Leadership',
  procurement: 'Procurement',
  viewer: 'Viewer',
};

function RoleBadge({ role }: { role: UserRole }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${roleColors[role]}`}
    >
      <Shield className="h-3 w-3" />
      {roleLabels[role]}
    </span>
  );
}

function SkillBadge({ skill }: { skill: PersonSkill }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-background-muted text-foreground-muted"
      title={skill.description || undefined}
    >
      <Wrench className="h-3 w-3" />
      {skill.name}
    </span>
  );
}

interface PersonCardProps {
  person: PersonListItem;
  onClick?: () => void;
}

export function PersonCard({ person, onClick }: PersonCardProps) {
  return (
    <div
      onClick={onClick}
      className={`bg-background rounded-lg border border-border p-4 transition-colors ${
        onClick ? 'cursor-pointer hover:border-primary/50 hover:bg-background-subtle' : ''
      } ${!person.isActive ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-foreground">{person.name}</h3>
              {!person.isActive && (
                <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                  Inactive
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 text-sm text-foreground-muted">
              <Mail className="h-3 w-3" />
              {person.email}
            </div>
          </div>
        </div>
        <RoleBadge role={person.role} />
      </div>

      {person.skills.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {person.skills.map((skill) => (
            <SkillBadge key={skill.id} skill={skill} />
          ))}
        </div>
      )}
    </div>
  );
}
